import { FormikContextType } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineExclamationCircle } from 'react-icons/ai';

import { ThemeColors } from 'models/general';
import { FlaggedCallBack } from 'utils/hooks/use-flagged-callback';
import MFormCard from 'views/MForm/MFormCard/MFormCard';
import MFormContainer from 'views/MForm/MFormContainer/MFormContainer';
import MFormContent from 'views/MForm/MFormContent/MFormContent';
import MFormFieldWrap from 'views/MForm/MFormFieldWrap/MFormFieldWrap';
import MFormFooter from 'views/MForm/MFormFooter/MFormFooter';
import MSwitch from 'views/MSwitch/MSwitch';

import { IUserProfileSecurityFormFields } from './SecurityTab.model';
import classes from './SecurityTab.module.scss';

type Props<Fields extends IUserProfileSecurityFormFields> = {
  formInstance: FormikContextType<Fields>;
  isLoading: boolean;
  isSite2FAEnforced: boolean;
  onIs2FAEnforcedToggle: FlaggedCallBack<(is2FAEnforced: boolean) => void>;
  display2FAWarning: boolean;
  multipleSitesInfo: boolean;
};

const SecurityTabView = <Fields extends IUserProfileSecurityFormFields>(props: React.PropsWithChildren<Props<Fields>>): JSX.Element => {
  const { t } = useTranslation();

  return (
    <div className={classes['container']}>
      <MFormContainer title={t('userProfile.tabs.security')} className={classes['formContainer']}>
        <MFormContent formik={props.formInstance} isLoading={props.isLoading}>

          <MFormCard header={t('userProfile.form.preferences.title')}>
            <MFormFieldWrap
              className={classes['switch']}
              name="is2FAEnforced"
              displayName={t('userProfile.form.preferences.twoFactorAuth')}
            >
              <MSwitch
                checked={props.isSite2FAEnforced || props.formInstance.values.is2FAEnforced}
                onChange={props.onIs2FAEnforcedToggle}
                color={ThemeColors.Info}
                disabled={props.isSite2FAEnforced}
              ></MSwitch>
              {props.display2FAWarning && (
                <div className={classes.twoFactorAuthWarning}>
                  <AiOutlineExclamationCircle className={classes.icon} />
                  {t('userProfile.form.preferences.twoFactorAuthWarning')}
                </div>
              )}
              {props.isSite2FAEnforced && (
                <div className={classes.twoFactorAuthNotice}>
                  <AiOutlineExclamationCircle className={classes.icon} />
                  {t('userProfile.form.preferences.twoFactorAuthNotice')}
                </div>
              )}
              {!props.isSite2FAEnforced && !props.formInstance.values.is2FAEnforced && props.multipleSitesInfo && (
                <div className={classes.twoFactorAuthNotice}>
                  <AiOutlineExclamationCircle className={classes.icon} />
                  {t('userProfile.form.preferences.twoFactorAuthInfo')}
                </div>
              )}
            </MFormFieldWrap>
          </MFormCard>

          <MFormFooter confirmText={t('userProfile.form.update')}></MFormFooter>
        </MFormContent>
      </MFormContainer>
    </div>
  );
};

SecurityTabView.displayName = 'SecurityTab';
SecurityTabView.defaultProps = {};

export default SecurityTabView;
